import { CardActions, CardContent, makeStyles, useTheme } from '@material-ui/core'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { CardWithTransition } from '../../../../components/shared/cardWithTransition/CardWithTransition'
import { LoadingCard } from '../../../../components/shared/loadingCard/loadingCard'
import { styles } from './priceComponentsCard.styles'

import { EmptyContent } from '../../../clients/file/components/shared/emptyCard'
import { Header } from '../../../clients/file/components/shared/header'

import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faMoneyCheckDollar } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormDialogMode } from '@globaleris/ui.library'
import { useSelector } from 'react-redux'
import { EnumCommodity, EnumPriceComponentReference } from '../../../../api/Argentia/enums'
import { ContractModel } from '../../../../api/Argentia/model/contracts'
import { EnumPriceType } from '../../../../api/oData/enums/enumPriceType'
import { PriceComponentsTable } from '../../../../components/shared/priceComponentsTable/priceComponentsTable'
import usePriceComponents from '../../../../components/shared/priceComponentsTable/priceComponentsTable.hooks'
import { priceComponentsTableModelListMapper } from '../../../../components/shared/priceComponentsTable/priceComponentsTable.mapper'
import { ButtonOption, SplitButton } from '../../../../components/shared/splitButton/splitButton'
import { StandardCardTooltip } from '../../../../components/shared/standardCard/standardCardToolTip/standardCardToolTip'
import { enumEnumToValueNumber } from '../../../../helpers'
import { NonAuthRoutes } from '../../../../model'
import { selectClientContractByID } from '../../../../redux/selectors/client.selectors'
import {
  ComponentCardAddButtonOptions,
  PriceComponentsCardProps
} from './priceComponentsCard.types'

export const PriceComponentsCard: React.FC<PriceComponentsCardProps> = ({
  contractId,
  flexibilityId,
  leadId,
  commodityId,
  style,
  insideTooltip = false,
  ...props
  // onClickAdd,
  // onClickEdit,
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'clientFile', 'priceComponent', 'enums'])
  const history = useHistory()

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const {
    priceComponents,
    isFetchingPriceComponents,
    refreshPriceComponents,
    errorFetchPriceComponents,
    price
  } = usePriceComponents({
    contractID: flexibilityId ? undefined : contractId,
    flexibilityID: flexibilityId,
    leadID: leadId,
    preventDefault: true
  })

  const contract = useSelector(state => {
    return selectClientContractByID(state, contractId)
  }) as ContractModel

  const commodity = (contractId && contract?.commodity) || (leadId && commodityId)

  const FlexibilitiesHeader = () =>
    React.useMemo(() => {
      return (
        <Header
          avatarIcon={<FontAwesomeIcon size="lg" icon={faMoneyCheckDollar as IconProp} />}
          title={t('clientFile:components.priceComponentsCard.title')}
          titleVariant="secondary"
        />
      )
    }, [t])

  if (isFetchingPriceComponents) {
    return <LoadingCard />
  }

  const getPriceComponentReference = (): EnumPriceComponentReference | undefined => {
    const priceComponentReferenceArray: [number | undefined, EnumPriceComponentReference][] = [
      [flexibilityId, EnumPriceComponentReference.Flexibilidad],
      [contractId, EnumPriceComponentReference.Contrato],
      [leadId, EnumPriceComponentReference.Lead]
    ]

    const priceComponentReferenceValue = priceComponentReferenceArray.find(
      ([key]) => key !== undefined
    )?.[1]
    return priceComponentReferenceValue
  }

  const handleClickAddButton = (option: ComponentCardAddButtonOptions) => {
    switch (option) {
      case ComponentCardAddButtonOptions.PARAMETER:
        history.push(NonAuthRoutes.PriceComponentsAdd, {
          contractId,
          flexibilityId,
          leadID: leadId, // needed to associate a priceComponent with a LeadId
          commodity: commodityId, // needed to fetch LeadOptions
          priceType: EnumPriceType.Parametro,
          priceComponentTypeReference: getPriceComponentReference(),
          formMode: FormDialogMode.ADD, // needed to create priceComponent with V2 when using leadId
          componentTypesToExclude: priceComponents
            .filter(component => component.priceType === EnumPriceType.Parametro)
            .map(component => component.idPriceComponentType)
        })
        break
      case ComponentCardAddButtonOptions.COEFFICIENT:
        history.push(NonAuthRoutes.PriceComponentsAdd, {
          contractId,
          flexibilityId,
          leadID: leadId, // needed to associate a priceComponent with a LeadId
          commodity: commodityId, // needed to fetch LeadOptions
          priceType: EnumPriceType.Apuntamiento,
          priceComponentTypeReference: getPriceComponentReference(),
          formMode: FormDialogMode.ADD, // needed to create priceComponent with V2 when using leadId
          componentTypesToExclude: priceComponents
            .filter(component => component.priceType === EnumPriceType.Apuntamiento)
            .map(component => component.idPriceComponentType)
        })
        break
      case ComponentCardAddButtonOptions.PARAMETER_PERIOD:
        history.push(NonAuthRoutes.PriceComponentsAdd, {
          contractId,
          flexibilityId,
          leadID: leadId, // needed to associate a priceComponent with a LeadId
          commodity: commodityId, // needed to fetch LeadOptions
          priceType: EnumPriceType.ParametroPeriodo,
          priceComponentTypeReference: getPriceComponentReference(),
          formMode: FormDialogMode.ADD,
          componentTypesToExclude: priceComponents
            .filter(component => component.priceType === EnumPriceType.ParametroPeriodo)
            .map(component => component.idPriceComponentType)
        })
        break
    }
  }

  const getAddButtonOptions = () => {
    let options = [
      {
        id: 1,
        name: ComponentCardAddButtonOptions.PARAMETER,
        label: t('clientFile:components.priceComponentsCard.actions.createParameter')
      }
    ]

    if (
      flexibilityId ||
      (contractId &&
        enumEnumToValueNumber(EnumCommodity, commodity) === EnumCommodity.Electricidad) ||
      (leadId && enumEnumToValueNumber(EnumCommodity, commodity) === EnumCommodity.Electricidad)
    ) {
      options.push(
        {
          id: 2,
          name: ComponentCardAddButtonOptions.COEFFICIENT,
          label: t('clientFile:components.priceComponentsCard.actions.createMargin')
        },
        {
          id: 3,
          name: ComponentCardAddButtonOptions.PARAMETER_PERIOD,
          label: t('clientFile:components.priceComponentsCard.actions.createParameterPeriod')
        }
      )
    }

    return options as ButtonOption[]
  }

  return (
    <>
      <CardWithTransition show={true} raised {...props} style={style}>
        <FlexibilitiesHeader />
        {errorFetchPriceComponents || (priceComponents && priceComponents.length > 0) ? (
          <>
            <CardContent className={classes.cardContent}>
              <PriceComponentsTable
                priceComponents={priceComponentsTableModelListMapper(priceComponents)}
                onRefreshPriceComponents={refreshPriceComponents}
                onNotificationChange={() => {}}
                errorResponse={errorFetchPriceComponents}
              />
              {insideTooltip && (
                <StandardCardTooltip
                  style={{ marginLeft: 0 }}
                  noIcon
                  tooltipCaption={t(
                    'clientFile:components.priceComponentsCard.cardTooltip.tooltipCaption'
                  )}
                />
              )}
            </CardContent>
            <CardActions className={classes.cardActionsRight}>
              <SplitButton
                options={getAddButtonOptions()}
                onClick={option =>
                  handleClickAddButton(option.name as ComponentCardAddButtonOptions)
                }
              />
            </CardActions>
          </>
        ) : (
          <EmptyContent title={t('clientFile:components.priceComponentsCard.emptyCard.title')}>
            <SplitButton
              options={getAddButtonOptions()}
              onClick={option => handleClickAddButton(option.name as ComponentCardAddButtonOptions)}
            />
          </EmptyContent>
        )}
      </CardWithTransition>
      {!insideTooltip && priceComponents.length > 0 && (
        <StandardCardTooltip
          style={{ marginLeft: 0 }}
          tooltipCaption={t('clientFile:components.priceComponentsCard.cardTooltip.tooltipCaption')}
        />
      )}
    </>
  )
}
